/**
 * THE ALERTS DASHBOARD — the panel at the top of a dashboard, and the first thing anyone reads.
 *
 * Everything else a dashboard shows is a count: alerts today, people on site, cameras online.
 * Counts answer "how is it going"; nobody opens a security dashboard to read those first. They
 * open it to find out whether something is happening RIGHT NOW. So this panel sits above the
 * analytics sheet and answers that question in three registers, top to bottom, each one a
 * different distance from the present:
 *
 *   THE HEAD — how much is open, broken down by severity, and the way out to the board and the
 *   list. The severity chips are a filter as well as a reading: "Critical 2" is information
 *   before it is a control, which is why it is a segmented track and not a dropdown.
 *
 *   THE VITALS — five readings that put the queue in proportion. A backlog figure with nothing
 *   beside it cannot be judged: nine thousand alerts raised in the last hour is a camera
 *   misbehaving, and nine thousand going back a week is a queue nobody is working. So the row
 *   states the last hour AGAINST the hour before it, names the zone producing most of them, says
 *   how tightly spaced they are, ages the backlog, and draws the last twelve hours as a pulse.
 *
 *   THE TIMELINE — the alerts themselves, newest first, on a rail that says how far apart they
 *   are. This is the part an operator acts on.
 *
 * ── The vitals are explicit about what they are counting ──
 *
 * Two of the five are computed from the LOADED FEED rather than from the node, because they are
 * questions no endpoint answers: which zone is loudest, and how tightly the alerts are spaced.
 * The feed is the newest few dozen alerts, not the whole queue — so both cells say so on their
 * own face ("41 of 78 shown"). A figure derived from a sample and presented as a total is the
 * single fastest way to make an operator stop trusting a dashboard, and the fix is not to hide
 * the cell but to state its scope where the number is.
 *
 * The other three come from the node's own aggregate (`stats`), which is the only place a true
 * total can come from. Where it hasn't arrived the cell says so rather than showing a zero.
 *
 * ── The pulse is 15 minutes wide, and that is the point ──
 *
 * The dashboard already has an hourly alert chart on its analytics sheet — the day's shape. This
 * is a different question: is it kicking off right now? An hour-wide bar cannot answer it, because
 * a burst that started twenty minutes ago is a third of a bar and looks exactly like a quiet hour
 * with the same count spread evenly through it. See `alertPulse` in analyticsSummaryService.
 *
 * ── The timeline: a rail, then the cards ──
 *
 * ONE horizontal rule with a marker per alert in its severity, the interval between neighbours
 * written on the rail between their markers, and each alert's card hanging below its own marker.
 * The spacing is UNIFORM and the time is STATED, because a rail scaled to real time would stack
 * four alerts from one incident on top of each other and then leave a hand's width of bare rule
 * before the next — and "four inside a minute" is exactly what the operator is reading the order
 * for. The rail also carries the day break where the queue crosses midnight, which is a different
 * kind of statement from an interval and the one an operator coming onto shift looks for.
 *
 * ONE LANE, NOT TWO. An earlier version alternated cards above and below the rule to fit twice as
 * many, which worked at thumbnail size and stops working here: these cards carry a real frame, and
 * a zig-zag of them is a wall rather than a queue. The cards are big enough to be read from a
 * step back, and the price is that fewer fit — which is what the pager is for.
 *
 * PAGED, with the range stated. `1–5 of 9,162` is the whole navigational story: how much is on
 * screen, and how much is behind it. Previews are resolved for the page on screen and for no
 * others — on the cloud every one of those is a blob through the tunnel — and pages past the live
 * feed are pulled through `onLoadMore` as the operator reaches them.
 *
 * ── One target per card ──
 *
 * The whole card opens the alert. No acknowledge/resolve chips: acting on an alert is done from
 * its record or from the list, where there is room to see what is being acknowledged. Resolving
 * the wrong alert from a dashboard tile is a real mistake; opening the wrong one costs a glance.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRightIcon,
  ArrowTrendingDownIcon,
  BellAlertIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FireIcon,
  FunnelIcon,
  HandRaisedIcon,
  PhotoIcon,
  ShieldExclamationIcon,
  TruckIcon,
  UserIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import type { HexAlert } from '../../api/contracts';
import { MiniBars } from './MiniCharts';

/**
 * Severity, in the five places this panel says it.
 *
 * `dot`    the rail's marker — the severity at full strength, on the line.
 * `border` the card's top arc, which follows its corner radius into the thin side borders. The
 *          card's own statement of severity, and the reason the badge below it need not shout.
 * `badge`  the word, over the frame. A SOFT TINT, not the solid fill it used to be: this panel
 *          draws these by the dozen, and five saturated blocks down a row read as five alarms
 *          when the arc above each one has already said which is which. Light-on-dark in both
 *          themes, so it stays legible over video.
 * `text`   the event-type symbol beside the alert's name — one glyph carrying both what tripped
 *          and how bad, in a step that clears 3:1 on either card surface.
 * `ring`   a thumbnail's outline, for hosts that draw one.
 */
const SEV: Record<string, { badge: string; ring: string; label: string; dot: string; text: string; border: string }> = {
  // `dot` is the same three severities as a solid — the timeline's markers, and the chips' in
  // the head.
  //
  // `text` is the severity as a FOREGROUND colour, and it exists for exactly one thing: the type
  // symbol that leads a stop's name (see `StopBlock`). It is the only place on a stop where the
  // two questions an operator triages on — what kind of thing tripped, and how bad is it — are
  // answered by one mark, and colouring the symbol is what fuses them: a flame in red is a
  // different object from a flame in blue, read before either word is.
  //
  // BOTH THEMES, EXPLICITLY. The card is `#ffffff` in light and `#1a1e2a` in dark, and no single
  // step of a palette clears 3:1 on both — `amber-500` is 2.2:1 on white, `blue-600` is 2.6:1 on
  // the dark card. So each is the darker step with a lighter one behind `dark:`, which is the
  // convention the rest of the shared components already use.
  critical: {
    badge: 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200',
    ring: 'ring-red-500/60',
    label: 'Critical',
    dot: 'bg-red-600',
    text: 'text-red-600 dark:text-red-400',
    border: 'border-t-red-600',
  },
  warning: {
    badge: 'bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
    ring: 'ring-amber-400/50',
    label: 'Warning',
    dot: 'bg-amber-500',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-t-amber-500',
  },
  normal: {
    badge: 'bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
    ring: 'ring-blue-500/40',
    label: 'Info',
    dot: 'bg-blue-600',
    text: 'text-blue-600 dark:text-blue-400',
    border: 'border-t-blue-600',
  },
};

/**
 * The head's severity filter, as an ordered vocabulary.
 *
 * ALL FOUR ARE ALWAYS DRAWN, including the ones standing at zero. A filter row that hides its
 * empty options is a filter row that changes shape as alerts arrive, and "Critical 0" is the
 * single most useful thing this strip can say — it is the reading an operator came to the
 * dashboard for, and dropping the chip makes them infer it from an absence.
 *
 * The DOT carries the same colour the markers use, so the chip and the alerts it selects
 * are recognisably about the same field. "Info" rather than "Normal", matching the badge — the
 * wire tier is `normal`, the word an operator reads is not.
 */
const SEV_FILTERS: Array<{ key: SevFilter; label: string; dot: string }> = [
  { key: 'all', label: 'All', dot: '' },
  ...(['critical', 'warning', 'normal'] as const).map((k) => ({
    key: k as SevFilter,
    label: SEV[k].label,
    dot: SEV[k].dot,
  })),
];

type SevFilter = 'all' | HexAlert['severity'];

/**
 * How far back the strip will page BY ITSELF to fill a filtered row.
 *
 * Choosing "Critical" on a row holding none is a request to look past the live feed, and the
 * strip answers it by pulling pages until something matches — but "look further back" is not
 * "download the queue", and on a node with a thousand open alerts an unbounded scan is exactly
 * that. Past this many loaded alerts the row stops pulling on its own and waits to be scrolled,
 * which is the operator saying they do want more.
 */
const FILTER_SCAN_MAX = 120;

/**
 * THE COLOUR AND WEIGHT OF THE RAIL.
 *
 * NOT `border`. This line was `bg-border` and a single pixel wide, and `--border` is
 * `rgba(0, 0, 0, 0.07)` in light and `rgba(255, 255, 255, 0.08)` in dark: a token built for the
 * edge of a card, where the job is to be *almost* invisible. That is the wrong job here. The rail
 * is not chrome around the content — on this panel it IS the content: the whole claim of the
 * design is that a queue of alerts reads as one object in time order, and at 7% black the object
 * holding them together could not be seen. The cards floated.
 *
 * The opacity trap rules out the obvious fix. These theme colours are bare `var()`s, and Tailwind
 * v3 emits NO rule at all for an opacity modifier on one — `bg-muted-foreground/40` is not a
 * dimmed line, it is no line. So the rail takes a numeric palette step, which does take modifiers
 * and, more importantly, can be pinned per theme.
 *
 * AS LIGHT AS THE REFERENCE, WHICH IS VERY LIGHT. The mock's rule samples at `#E4E8EE` — all but
 * exactly `slate-200`. It went through `slate-500` and then `slate-400` on the way here, and both
 * were too assertive for what this line does now: with the stalks gone (see `Rail`) and a marker
 * over every card, a dark rule stops reading as a spine and starts reading as an underline
 * cutting the panel in two. The markers carry the weight; the rule only has to connect them.
 *
 * WHAT KEEPS IT FROM VANISHING is the 2px, not the colour. The version that was genuinely
 * invisible was `bg-border` at ONE pixel — `rgba(0, 0, 0, 0.07)`, which over white is about
 * `#EDEDED`, a shade off this and half the thickness. At two pixels a `slate-200` rule reads as a
 * clean hairline the way a table divider does, which is the job.
 *
 * The steps invert between themes for the usual reason: `slate-200` is a soft line on a white card
 * and invisible on `#1a1e2a`, and `slate-700` is the equivalent step away from the dark surface.
 */
const RAIL_LINE = 'bg-slate-200 dark:bg-slate-700';

/** The rail's thickness. 2px rather than 1px: at one pixel the line survives only on a high-DPI
 *  screen at full brightness, and this panel is read on control-room monitors. */
const RAIL_W = 'h-0.5';


/**
 * A stretch of time, named the way someone would say it out loud.
 *
 * The day is stated ONCE when both ends fall on it ("08:52 pm to 10:54 pm, Today") and twice when
 * they don't ("Yesterday 11:40 pm to Today 10:54 pm"). Naming it on both ends unconditionally
 * produced "08:52 pm on Today through 10:54 pm on Today", which reads like a machine.
 */
function spanLabel(from: number, to: number): string {
  return sameDay(from, to)
    ? `${fmtClock(from, false)} to ${fmtClock(to, false)}, ${dayLabel(to)}`
    : `${dayLabel(from)} ${fmtClock(from, false)} to ${dayLabel(to)} ${fmtClock(to, false)}`;
}

/** "1 alert" / "9 alerts" — a count with its noun agreeing with it. */
function plural(count: number, one: string, many = `${one}s`): string {
  return `${n(count)} ${count === 1 ? one : many}`;
}

/** Where the alert happened, as the operator names the place: camera, then site. */
export interface AlertPlace {
  name?: string;
  site?: string;
}

/**
 * A bucketed series as the node's summary sends it: `start` is the epoch second of the first
 * bucket, `step` its width in seconds, `values` one count per bucket, oldest first.
 */
export interface AlertPulse {
  from: number;
  to: number;
  hours: number;
  total: number;
  /** Stated by the node so the chart's scale and the "peak N" beside it cannot disagree. */
  peak: number;
  series: { start: number; step: number; values: number[] };
}

/**
 * The figures only the NODE can answer, from its own aggregate over every alert rather than over
 * the handful this panel holds. Omit it and those cells say so instead of showing a zero — an
 * unanswered question and an answer of nothing must never look the same on a dashboard.
 */
export interface AlertStats {
  /** Open alerts still in `new` — nobody has picked them up. The "needs review" figure. */
  unacked?: number;
  /** Epoch seconds of the OLDEST open alert. What ages the backlog. */
  oldest?: number | null;
  /** Alerts per quarter-hour over the last twelve hours. */
  pulse?: AlertPulse | null;
}

export interface LatestAlertsStripProps {
  /** Active alerts, newest first. A page of them is drawn — see `minCardWidth`. */
  alerts: HexAlert[];
  /** Total open on the node — the head's pill, and the denominator of the position readout.
   *  ≥ alerts.length. */
  open: number;
  /** No feed yet: placeholders, never the empty state. */
  loading?: boolean;
  /** The node's own aggregates for the vitals row. See `AlertStats`. */
  stats?: AlertStats | null;
  /**
   * Is the feed live right now?
   *
   * Purely a statement about the SUBSCRIPTION, not about whether anything is arriving: a quiet
   * night and a dropped socket produce the same still panel, and only one of them means the
   * operator is looking at something stale. Omit on hosts that can't tell.
   */
  live?: boolean;
  /** Narrowest a card may be, in px. The row fits as many as it can and lets them share out the
   *  remainder, so the timeline is flush at any window width. */
  minCardWidth?: number;
  /** Ceiling on the cards ONE PAGE may show, however many would fit — so an unusually wide wall
   *  display doesn't put the whole queue on screen at once. */
  maxCards?: number;
  /** cameraId → where it is. Missing entries just render the alert without a place. */
  places?: Record<string, AlertPlace>;
  /** Lazy preview resolver for hosts whose snapshots aren't directly loadable (cloud). */
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
  /** Open one alert's record. */
  onOpenAlert?: (alert: HexAlert) => void;
  /** The live board (honeycomb / floor plan) — the head's button. Omit if unreachable. */
  onOpenBoard?: () => void;
  /** The full alert LIST, hung off the "N open" pill — the one thing in the head that is already
   *  about the whole queue rather than about any one picture. Omit and the pill is plain text. */
  onViewAll?: () => void;
  /**
   * Fetch the next page of older alerts. Called when the pager reaches the end of what is loaded
   * while `open` says the node has more — never on mount, and never for a page the operator has
   * not reached. Omit on hosts that can't page and the pager stops at the live feed.
   */
  onLoadMore?: () => void;
  /** A page is in flight: the cells it will fill render as placeholders. */
  loadingMore?: boolean;
  className?: string;
}

/** The open-count pill, which is a button when the host can open the list and plain text
 *  otherwise — identical either way, because the count is the same statement.
 *
 *  The tint is `red-500/15` and NOT `bg-destructive/10`: `destructive` is a bare `var()` in the
 *  preset, and Tailwind v3 emits no rule at all for an opacity modifier on one of those, so the
 *  pill had no background whatsoever and the count read as loose red type beside the heading.
 *  Numeric palette colours take modifiers fine. */
const PILL =
  'rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide tabular-nums text-destructive';

/** The narrowest a card may be before the row would rather show fewer of them. */
const MIN_CARD_W = 196;

/** Widest a card is allowed to get on a very wide display. Past this the row shows MORE alerts
 *  rather than stretching five of them across a wall. */
const MAX_CARD_W = 260;

/** The gap between cards, and between the rail's markers — one number, because a marker sits at
 *  the centre of its own card and the two grids must not drift apart. */
const CARD_GAP = 12;

/**
 * HOW WIDE A CARD IS DRAWN WHEN THE POINTER IS ON IT.
 *
 * The frame in the row is ~200px across — enough to say WHICH camera view this is, not enough to
 * see what is in it. Rather than make every card readable, which would put three alerts on a
 * screen, pointing at one redraws it at this width. Looking closer costs nothing and the row
 * keeps its density.
 *
 * A REDRAW, NOT A `scale()` TRANSFORM. A transform was the first attempt and it was clipped: the
 * cards live in a horizontal scroller, and `overflow-x: auto` makes the vertical axis a clipping
 * context too whatever `overflow-y` says (see the note on the scroll box). It also blurs the type
 * — a scaled 12px label is a 12px label stretched, not a 16px one. Rendering the same markup at a
 * bigger width in a portal is both unclipped and sharp: the frame gets more pixels and the text
 * is set at its own size.
 *
 * 380 is about twice the row's card, which is where a 16:9 snapshot stops being a thumbnail
 * (214px of picture height). It is clamped to the viewport, so a narrow window gets whatever
 * there is rather than an enlargement running off the edge.
 */
const ZOOM_W = 380;

/** How far the enlargement stands off the viewport's edges when it has to be pulled back in. */
const ZOOM_GAP = 12;

/**
 * The badge's icon says WHAT KIND of thing tripped before the name is read — a person, a
 * vehicle, a fire — which is the distinction an operator triages on when two alerts share a
 * severity. Matched on the event type loosely (`person_detected`, `ppe-violation`,
 * `fire_smoke` are all the same family), falling back to the generic alert bell.
 */
const TYPE_ICONS: Array<[RegExp, typeof BellAlertIcon]> = [
  [/fire|smoke/, FireIcon],
  [/vehicle|anpr|plate|truck/, TruckIcon],
  [/ppe|helmet|vest/, ShieldExclamationIcon],
  [/theft|loiter/, HandRaisedIcon],
  [/fall/, ArrowTrendingDownIcon],
  [/face|person|people|intrusion|crowd|count/, UserIcon],
];

function typeIcon(alert: HexAlert): typeof BellAlertIcon {
  const key = `${alert.eventType || ''} ${alert.alertName || ''}`.toLowerCase();
  return TYPE_ICONS.find(([re]) => re.test(key))?.[1] || BellAlertIcon;
}

/**
 * The wall clock split into the digits and the meridiem, so a card can set them at two sizes.
 *
 * "10:05 PM" as one string makes the meridiem compete with the hour for the eye at the same
 * weight; the reference sets it two-thirds the size beside the digits, which is what makes
 * "10:05" the thing you land on. Locales with no meridiem (24-hour clocks) return an empty one
 * and the card simply prints the digits — never a stray marker where the locale has none.
 */
function fmtClockParts(ts?: number): { time: string; meridiem: string } {
  const whole = fmtClock(ts, false);
  const m = /^(.*?)\s*([AaPp]\.?[Mm]\.?)$/.exec(whole);
  return m ? { time: m[1], meridiem: m[2] } : { time: whole, meridiem: '' };
}

/**
 * The wall clock, to the second where there is room for it. There is not in a stop's headline,
 * which is 13px type in a 132px block beside a thumbnail: the seconds are the half of it an
 * operator never triages on. They are kept for the hover's enlargement, which has the width — the
 * exact second is what one alert is timed against another BY, once the operator is actually
 * looking.
 */
function fmtClock(ts?: number, seconds = true): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return new Date(ms).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' as const } : {}),
  });
}

function timeAgo(ts?: number): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const s = Math.floor(Math.max(0, Date.now() - ms) / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * THE DISTANCE IN TIME BETWEEN TWO NEIGHBOURING STOPS — the label that makes this a timeline
 * rather than a row of pictures in an order.
 *
 * The stops are evenly PITCHED: every alert gets the same column whether it landed a second or a
 * night after the one before it, because a strip scaled to real time would put four alerts from
 * one incident on top of each other and then leave a hand's width of empty rule before the next.
 * What is lost that way is the very thing the operator is reading the order FOR — four inside a
 * minute is an incident, one an hour later is a coincidence — so the interval is written on the
 * axis between the two markers instead. Uniform space, stated time.
 *
 * Nothing under a minute: the pill is there to say the two alerts are FAR apart, and "12s"
 * between neighbours is a decoration on the one case where the pictures already say it.
 */
function fmtGap(newer?: number, older?: number): string | null {
  if (!newer || !older) return null;
  const a = newer < 1e12 ? newer * 1000 : newer;
  const b = older < 1e12 ? older * 1000 : older;
  const s = Math.floor((a - b) / 1000);
  if (s < 60) return null;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function dayOf(ts: number): Date {
  return new Date(ts < 1e12 ? ts * 1000 : ts);
}

/** Two alerts on the same calendar day — the test the day pill is raised on. */
function sameDay(a?: number, b?: number): boolean {
  if (!a || !b) return true;
  const x = dayOf(a);
  const y = dayOf(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
}

/**
 * WHERE THE TIMELINE CROSSES MIDNIGHT, named the way the operator would say it. A shift handing
 * over is the one break in this queue that matters more than any interval: "everything left of
 * here is today" is read from one pill, where "17h" between two stops has to be worked out.
 */
function dayLabel(ts?: number): string {
  if (!ts) return '';
  const d = dayOf(ts);
  const now = new Date();
  const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (sameDay(ts, Math.floor(now.getTime() / 1000))) return 'Today';
  if (d.getFullYear() === y.getFullYear() && d.getMonth() === y.getMonth() && d.getDate() === y.getDate()) {
    return 'Yesterday';
  }
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/**
 * THE PREVIEW, resolved once and KEPT — as a hook, because two things draw it now: the small
 * square on the axis and the enlargement that answers a hover. One resolver for both is what makes
 * the hover free: the big picture is already in the browser when the pointer lands, and the cloud
 * pays for one blob through the tunnel instead of two.
 */
function useAlertPreview(
  alert: HexAlert,
  loadPreview: ((alert: HexAlert) => Promise<string | null>) | undefined,
  /** This stop has come within a screen's edge of being looked at. Until then it costs no
   *  request at all — see the track's note on why every stop in it is not a fetch. */
  active: boolean,
) {
  const directUrl = alert.snapshotUrl || null;
  // 0 until the node says the snapshot is stored. Alerts are broadcast before the storage
  // lane has written the image, so "no picture yet" is the normal first state, not a failure.
  const previewReady = alert.previewReady || 0;
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  // Resolve lazily WITHOUT blocking the stop, and revoke any object URL this created.
  useEffect(() => {
    setImgError(false);
    setResolvedUrl(null);
    if (directUrl || !loadPreview || !active) return;
    let cancelled = false;
    let made: string | null = null;
    loadPreview(alert)
      .then((url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url) {
          made = url;
          setResolvedUrl(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.id, directUrl, loadPreview, previewReady, active]);

  // A direct URL is gated the same way — on the local node it is a real request to the node's
  // snapshot store, and sixty of them to draw three stops is the same waste by another route.
  const src = !active || imgError ? null : directUrl || resolvedUrl;
  const onError = useCallback(() => setImgError(true), []);
  return { src, imgError, onError };
}

/**
 * The picture as it fills whatever box it is given — the square on the axis and the hover's
 * enlargement are the same layers at two sizes, so what the pointer summons is plainly the thing
 * it was pointing at.
 */
function AlertMedia({
  alert,
  src,
  imgError,
  onError,
  iconClass = 'h-6 w-6',
}: {
  alert: HexAlert;
  src: string | null;
  imgError: boolean;
  onError: () => void;
  iconClass?: string;
}) {
  const Icon = typeIcon(alert);

  return (
    <>
      {/* The dark ground is painted whether or not an image lands, so the scrim, the badge and
          the white type below are legible from the first frame and don't flip contrast when
          the picture arrives. */}
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-950" />
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={onError}
        />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center">
          {imgError ? (
            <PhotoIcon className={`${iconClass} text-white/20`} />
          ) : (
            <Icon className={`${iconClass} animate-pulse text-white/25`} />
          )}
        </span>
      )}
      {/* NO SCRIM. There was one along the bottom while the name and place were printed over the
          picture; those words are beside the picture now, and a gradient with nothing to hold up is
          just a greyed-out third of a 36px square. */}
    </>
  );
}


/* ─── The vitals ─────────────────────────────────────────────────────────── */

/**
 * ONE READING: a small caps label, the figure, and the one line that makes the figure mean
 * something. The subline is not decoration — a number on a dashboard with nothing to compare it
 * against is a number nobody can act on, and every cell here owes the reader either a baseline
 * ("up from 0"), a scope ("41 of 78 shown") or a unit ("between events").
 *
 * `quiet` is for a cell whose figure the node hasn't answered yet: the value goes muted and reads
 * as an em dash rather than as a zero, because "not known" and "none" are different facts and a
 * dashboard that draws them the same is lying in the cheapest possible way.
 */
function Vital({
  label,
  value,
  note,
  hint,
  quiet,
}: {
  label: string;
  value: string;
  note?: string;
  /**
   * The whole sentence, on hover.
   *
   * A vitals cell has room for a figure and four or five words, which is enough to say what a
   * number IS and not always enough to say what it was measured over. The note carries the scope
   * because that is the part a reader can be misled by; this carries the definition and the
   * window, for the reader who stops to ask. Nothing here is hover-ONLY — the cell still reads
   * correctly with no pointer.
   */
  hint?: string;
  quiet?: boolean;
}) {
  return (
    <div className="min-w-0 px-4 py-3" title={hint}>
      <p className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </p>
      <p
        className={`mt-1 truncate text-[22px] font-bold leading-none tabular-nums ${
          quiet ? 'text-muted-foreground' : 'text-foreground'
        }`}
        title={hint ? undefined : value}
      >
        {value}
      </p>
      {note && <p className="mt-1 truncate text-[11px] leading-[15px] text-muted-foreground">{note}</p>}
    </div>
  );
}

/** Compact counts: a dashboard figure that has run to four digits should not push its neighbours
 *  off the row, and nobody reads the exact value of a backlog off a tile. */
const n = (v: number) => v.toLocaleString();

/**
 * How long ago, in the coarsest unit that is still true — the backlog's age, so days are the
 * usual answer and seconds never are.
 */
function ageOf(ts?: number | null): string | null {
  if (!ts) return null;
  const s = Math.max(0, Math.floor(Date.now() / 1000 - (ts < 1e12 ? ts : ts / 1000)));
  if (s < 60) return 'under a minute';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)} days`;
}

/**
 * THE TYPICAL SPACING between the alerts on hand — the median gap, not the mean.
 *
 * The median because this distribution is exactly the shape a mean is worst at: a queue is
 * normally a few bursts separated by long quiet stretches, and one overnight gap of nine hours
 * drags a mean far away from anything that actually happened. The median says "half the gaps are
 * shorter than this", which is what "how fast are they coming" means.
 *
 * Under a minute it stops counting seconds and says so: at that spacing the exact figure is
 * noise, and "under 1m" is the whole message.
 */
function medianGap(alerts: HexAlert[]): { label: string; count: number; from: number; to: number } | null {
  const ts = alerts
    .map((a) => (a.timestamp ? (a.timestamp < 1e12 ? a.timestamp * 1000 : a.timestamp) : 0))
    .filter(Boolean)
    .sort((x, y) => y - x);
  if (ts.length < 2) return null;
  const gaps: number[] = [];
  for (let i = 1; i < ts.length; i += 1) gaps.push((ts[i - 1] - ts[i]) / 1000);
  gaps.sort((x, y) => x - y);
  const mid = gaps.length % 2 ? gaps[(gaps.length - 1) / 2] : (gaps[gaps.length / 2 - 1] + gaps[gaps.length / 2]) / 2;
  const label =
    mid < 60
      ? 'under 1m'
      : (() => {
          const m = Math.round(mid / 60);
          if (m < 60) return `${m}m`;
          const h = Math.floor(m / 60);
          return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
        })();
  // The two ends of the stretch it was measured over, so the cell can name the window rather than
  // leaving the reader to guess whether "12m" describes today, this shift, or the last four
  // alerts. `ts` is newest-first.
  return { label, count: ts.length, from: ts[ts.length - 1] / 1000, to: ts[0] / 1000 };
}

/**
 * WHICH PLACE IS PRODUCING MOST OF THEM, among the alerts on hand.
 *
 * Named by the camera's own place where there is one and by the camera id where there isn't,
 * because a zone the operator can't name is not an answer. Returns the count with it so the cell
 * can state its scope — this is a reading off a SAMPLE of the queue, and it has to say so.
 */
function busiestPlace(
  alerts: HexAlert[],
  places?: Record<string, AlertPlace>,
): { name: string; count: number } | null {
  const tally = new Map<string, number>();
  for (const a of alerts) {
    const p = places?.[a.cameraId];
    const name = p?.name || p?.site;
    if (!name) continue;
    tally.set(name, (tally.get(name) || 0) + 1);
  }
  let best: { name: string; count: number } | null = null;
  for (const [name, count] of tally) if (!best || count > best.count) best = { name, count };
  return best;
}

/**
 * The last hour against the one before it, read off the pulse.
 *
 * Derived from the series rather than asked for separately so the figure and the chart under it
 * can never disagree — they are the same numbers added up two ways. Four buckets is an hour at
 * the pulse's 15-minute step; the arithmetic follows `step` rather than assuming it, so a
 * server-side change to the bucket width can't silently turn this into "the last 40 minutes".
 */
function lastHour(pulse?: AlertPulse | null): { now: number; prev: number | null } | null {
  const s = pulse?.series;
  if (!s || !s.values.length || !s.step) return null;
  const per = Math.max(1, Math.round(3600 / s.step));
  const v = s.values;
  // Not even an hour of window: there is no "last hour" to report, which is a different thing
  // from an hour in which nothing happened.
  if (v.length < per) return null;
  const sum = (from: number, to: number) => v.slice(from, to).reduce((a, b) => a + b, 0);
  return {
    now: sum(v.length - per, v.length),
    /**
     * `null` unless there is a WHOLE earlier hour behind it.
     *
     * This used to clamp the start index to 0, which silently compared a full hour against
     * however much of one the window happened to hold — four buckets against two, reported as
     * though the two were the same measurement. A short baseline reads as a plunge in traffic
     * that never happened, and it does so most on a node that has just started up.
     */
    prev: v.length >= 2 * per ? sum(v.length - 2 * per, v.length - per) : null,
  };
}

/**
 * The last hour SET AGAINST THE ONE BEFORE IT, in words.
 *
 * The figure alone can't be judged — forty alerts in an hour is a busy shift or a camera coming
 * loose, and only the hour before it says which. This line used to be a hardcoded `up from N`,
 * which is a claim rather than a reading: with 1 this hour against 3 last hour it printed
 * "1 · up from 3", stating the opposite of what the numbers say. The direction now follows the
 * comparison.
 *
 * ONE CAVEAT WORTH KNOWING, and the reason a small fall is not worth reading much into: the
 * newest quarter-hour bucket is still filling. "Last hour" is the last four buckets of wall
 * clock — genuinely the last hour — but up to a quarter of it hasn't happened yet, while the hour
 * before it is complete. A steady rate therefore drifts slightly "down" late in a bucket and
 * catches up at the boundary. That is inherent to comparing a live hour with a finished one; the
 * alternative is a figure that lags the queue by fifteen minutes, which on an ALERT dashboard is
 * the worse trade.
 */
function trendNote(now: number, prev: number | null): string {
  if (prev == null) return 'no earlier hour to compare';
  if (now === prev) return 'same as the hour before';
  return `${now > prev ? 'up' : 'down'} from ${n(prev)}`;
}


/* ─── The rail ───────────────────────────────────────────────────────────── */

/**
 * THE RAIL — one hairline across the whole track, a solid marker per alert in its severity, and
 * the interval between neighbours written on the line between their markers.
 *
 * IT LIVES INSIDE THE SCROLL BOX, on the same grid as the cards (one equal column each,
 * `CARD_GAP` between), so a marker sits exactly over the card it belongs to and stays there as
 * the row is dragged. A rail whose beads don't track their cards is worse than no rail.
 *
 * NO STALKS. An earlier pass dropped a connector from each marker to its card, which reads
 * correctly when the cards are thumbnails packed two-deep and reads as clutter here: the cards
 * are wide, their severity is already stated by the arc across their top, and the marker is
 * directly above the card it names. The reference design has bare space between the line and the
 * cards, and it is right — the alignment is the connection.
 *
 * SOLID MARKERS, not ringed dots. The disc is the severity at full strength; `ring-card` punches
 * the line out around it so it reads as a point ON the rail rather than a bead lying across it.
 * The white pip that used to sit inside each one made a 14px marker into a tiny target-shape
 * that competed with the badge on the card below.
 *
 * THE LINE IS A HAIRLINE IN THE LIGHTER STEP — `STALK_LINE`, not the darker `AXIS_LINE` this
 * used to take. With no stalks crossing it and a marker every column, a heavy rule stopped being
 * a spine and started being an underline dividing the panel in two. It is still 2px in a real
 * grey (never the `border` token, which at 7% black is invisible — see the note on those
 * constants); it is simply one step back, which is what the reference shows.
 *
 * The interval sits at the LEFT edge of each column — exactly halfway between that marker and the
 * previous one, since each marker is centred in its own column. Nothing is written under a minute
 * (`fmtGap`): the label exists to say two alerts are FAR apart. A day break outranks an interval
 * and is said differently, because "which shift" is a different question from "how long".
 */
function Rail({
  items,
  cardW,
  prevOf,
}: {
  items: Array<HexAlert | null>;
  cardW: number;
  prevOf: (i: number) => number | undefined;
}) {
  return (
    <div aria-hidden className="relative flex items-center" style={{ height: 28, gap: CARD_GAP }}>
      {/* The rule, spanning the whole TRACK — it runs on past the first and last markers to the
          ends of the scrollable width, which is what makes the queue read as continuing rather
          than as starting and stopping at the alerts that happen to be loaded. */}
      <span className={`pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 ${RAIL_W} ${RAIL_LINE}`} />
      {items.map((a, i) => {
        const sev = a ? SEV[a.severity || 'normal'] || SEV.normal : null;
        const prev = a ? prevOf(i) : undefined;
        const dayBreak = a && prev != null && !sameDay(prev, a.timestamp);
        const interval = !a ? null : dayBreak ? dayLabel(a.timestamp) : fmtGap(prev, a.timestamp);
        return (
          <div key={i} className="relative flex-none self-stretch" style={{ width: cardW }}>
            {interval && (
              <span
                className={`pointer-events-none absolute left-0 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap bg-card px-1.5 text-[11px] leading-[16px] tabular-nums ${
                  dayBreak
                    ? 'rounded-full border border-slate-300 font-semibold uppercase tracking-wide text-foreground dark:border-slate-600'
                    : 'font-medium text-muted-foreground'
                }`}
                style={{ marginLeft: -CARD_GAP / 2 }}
              >
                {interval}
              </span>
            )}
            <span
              className={`absolute left-1/2 top-1/2 z-10 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-[3px] ring-card ${
                sev ? sev.dot : RAIL_LINE
              }`}
            />
          </div>
        );
      })}
    </div>
  );
}

/* ─── The card ───────────────────────────────────────────────────────────── */

/**
 * ONE ALERT: its frame, when it happened, what tripped, and where.
 *
 * THE SEVERITY IS THE CARD'S TOP BORDER — a 3px arc that follows the card's own corner radius
 * into the thin border down each side. Not a bar laid across the top: the arc is what makes the
 * colour read as belonging to the card rather than sitting on it, and it is the only
 * full-strength block of alert colour on the card that isn't a word.
 *
 * THE TIME IS THE HEADLINE, in plain `foreground` and NOT in a colour of any kind. It was `brand`
 * for a while, and brand on this theme is a vermilion the token file itself describes as reading
 * as red beside `--destructive`: a row of near-red clocks on a panel whose whole vocabulary is
 * "red means critical" is a false alarm on every card, including the ones standing at Info. The
 * clock wins its line on size and weight; it needs no hue. The meridiem rides at a smaller size
 * beside it, so "10:05" is what the eye lands on and "PM" is available without competing.
 *
 * THE EVENT'S OWN SYMBOL leads the name — a person, a flame, a vehicle — in the severity's colour,
 * so ONE glyph answers both triage questions at once: what kind of thing tripped, and how bad. It
 * matters most on exactly the row where reading is slowest, fourteen cards all named "person
 * crossing detected", where the words have stopped distinguishing anything and the shape has not.
 *
 * NO DURATION CHIP. The reference design carries one ("8s") in the footer; `alerts` has no
 * duration column and `HexAlert` no duration field, so there is nothing true to put there. The
 * slot holds the alert's STATUS instead, and only once somebody has picked it up — "who is already
 * on this" is the thing a dashboard reader actually needs from a footer, and a chip reading "new"
 * on every card would be noise.
 *
 * ── THE HOVER ENLARGES THE CARD ──
 *
 * The frame is ~200px wide in the row, which is enough to say WHICH camera view this is and not
 * enough to see what is in it. Rather than make every card big enough to read — which would put
 * three alerts on a screen — pointing at one scales it up in place, so looking closer costs
 * nothing and the row keeps its density.
 *
 * A TRANSFORM, not a size change: `scale` is composited and affects no other card's position, so
 * the row does not reflow and the horizontal scroll offset does not jump under the pointer. Layout
 * animation of a card inside a scroll box is exactly the thing that makes a slider feel broken.
 *
 * THE ORIGIN FOLLOWS THE CARD'S PLACE IN THE ROW — `bottom left` for the first card, `bottom
 * right` for the last, `bottom` for the rest. Scaling from the centre would push the leftmost card
 * off the left edge of the scroll box, where it is clipped rather than enlarged; anchoring it to
 * the edge it is already against grows it inwards, into the space that exists. `bottom` in every
 * case because the rail is directly above and a card growing upwards would swallow its own marker.
 */
function AlertCard({
  alert,
  place,
  width,
  edge,
  loadPreview,
  onOpen,
}: {
  alert: HexAlert;
  place?: AlertPlace;
  width: number;
  /** Where this card sits in the visible row, so the hover grows inwards at the ends. */
  edge: 'start' | 'end' | 'middle';
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
  onOpen?: (alert: HexAlert) => void;
}) {
  const sev = SEV[alert.severity || 'normal'] || SEV.normal;
  const TypeIcon = typeIcon(alert);
  const name = alert.alertName || alert.eventType || 'Alert';
  const at = place?.name || place?.site;
  const { time, meridiem } = fmtClockParts(alert.timestamp);
  const age = timeAgo(alert.timestamp);
  /** Picked up by somebody. `new` is the default and says nothing worth a chip. */
  const owned = alert.status && alert.status !== 'new' ? alert.status : null;

  /**
   * The frame is fetched only once the card is actually on screen — on the cloud every preview is
   * a blob through the tunnel, and a track holding sixty cards would otherwise pay for sixty of
   * them to draw the five in view. One observer per card, disconnected the moment it fires, so a
   * picture already in hand is never re-fetched and can't flicker back to its placeholder as the
   * row is dragged over it.
   */
  const frameRef = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen) return;
    const el = frameRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setSeen(true);
      },
      { rootMargin: '0px 300px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [seen]);

  const { src, imgError, onError } = useAlertPreview(alert, loadPreview, seen);

  /**
   * The card's contents, as a FUNCTION rather than an element, because they are drawn twice — once
   * in the row and once in the enlargement's portal — and only one of those copies may carry the
   * frame's observer ref. React attaches a ref per rendered instance, so a shared element would
   * hand `frameRef` to whichever copy mounted last: the portal. It would then go null the moment
   * the pointer left, and this card would never resolve a preview again.
   */
  const body = (frameOf?: React.Ref<HTMLDivElement>) => (
    <>
      {/* THE FRAME, 16:9 — the aspect every camera in the product produces, so a snapshot fills it
          rather than being letterboxed into it. */}
      <div
        ref={frameOf}
        className="relative w-full overflow-hidden rounded-lg bg-neutral-900"
        style={{ aspectRatio: '16 / 9' }}
      >
        <AlertMedia alert={alert} src={src} imgError={imgError} onError={onError} iconClass="h-6 w-6" />
        {/* Severity in words. A soft tint rather than a solid fill, which is what the reference
            shows and what keeps five of these down a row from reading as five alarms — the arc
            across the card's top is already the full-strength statement. Light-on-dark either
            way, so it stays legible over video. */}
        <span
          className={`absolute right-2 top-2 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${sev.badge}`}
        >
          {sev.label}
        </span>
        {/* WHICH camera, over the frame's foot. `ring-1` rather than a scrim: a light chip on a
            dark frame is the reference's shape, and the ring is what keeps it off an overexposed
            one.

            SOLID `bg-card`, not the `bg-card/95` this was first written as. `card` is a bare
            `var()` in the preset and Tailwind v3 emits NO rule for an opacity modifier on one, so
            that chip had no background whatsoever — grey uppercase type floating directly on the
            video, unreadable over anything but a dark frame. The same trap as `bg-destructive/10`
            on the open pill. A fully opaque chip is what the reference shows anyway. */}
        {at && (
          <span className="absolute bottom-2 left-2 max-w-[85%] truncate rounded-md bg-card px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ring-1 ring-black/10">
            {at}
          </span>
        )}
      </div>

      {/* WHEN — the headline, with the relative age beside it. Both, because they answer different
          questions: "10:05 PM" is what you quote in a handover and "just now" is what tells you
          whether to get up. */}
      <div className="mt-2.5 flex items-baseline justify-between gap-2">
        <span className="flex min-w-0 items-baseline gap-1">
          <span className="truncate text-[19px] font-bold leading-none tabular-nums text-foreground">
            {time}
          </span>
          {meridiem && (
            <span className="flex-none text-[11px] font-bold uppercase leading-none text-foreground">
              {meridiem}
            </span>
          )}
        </span>
        <span className="flex-none text-[11px] leading-none text-muted-foreground">{age}</span>
      </div>

      {/* WHAT, led by its symbol in the severity's colour. */}
      <div className="mt-2 flex items-center gap-2">
        <TypeIcon className={`h-4 w-4 flex-none ${sev.text}`} aria-hidden />
        <span className="truncate text-[12px] text-foreground" title={name}>
          {name}
        </span>
      </div>

      {/* WHERE, and who has it. No divider: the reference leaves bare space, and a rule here
          would be the fourth horizontal line inside a 200px card. */}
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
          <VideoCameraIcon className="h-4 w-4 flex-none" aria-hidden />
          <span className="truncate" title={at || undefined}>
            {at || 'Unknown camera'}
          </span>
        </span>
        {owned && (
          <span className="flex-none rounded-md border border-border px-2 py-0.5 text-[10px] font-semibold capitalize text-muted-foreground">
            {owned}
          </span>
        )}
      </div>
    </>
  );

  /**
   * THE HOVER, held as the CARD'S RECTANGLE ON SCREEN rather than as a boolean.
   *
   * The enlargement is positioned off the card — and the card moves: the track scrolls under the
   * pointer, the page scrolls under the track. Re-measuring on any scroll (capture, so the
   * track's own counts) keeps the big card attached to the small one instead of stranded where it
   * was summoned.
   */
  const cardRef = useRef<HTMLElement | null>(null);
  const [zoom, setZoom] = useState<{ left: number; top: number; width: number } | null>(null);
  const measure = useCallback(() => {
    const el = cardRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { left: r.left, top: r.top, width: r.width };
  }, []);
  const showZoom = useCallback(() => setZoom(measure()), [measure]);
  const hideZoom = useCallback(() => setZoom(null), []);

  useEffect(() => {
    if (!zoom) return;
    const follow = () => {
      const box = measure();
      // Same rectangle → same state object, so a scroll that doesn't move this card doesn't
      // re-render it either.
      setZoom((prev) =>
        !box || !prev || (box.left === prev.left && box.top === prev.top && box.width === prev.width)
          ? prev
          : box,
      );
    };
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [zoom, measure]);

  const shell = `relative block w-full overflow-hidden rounded-xl border border-border border-t-[3px] bg-card p-2 text-left transition-shadow duration-150 hover:shadow-card-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${sev.border}`;
  const hover = {
    onMouseEnter: showZoom,
    onMouseLeave: hideZoom,
    /* Keyboard gets the same enlargement — the big picture is not a mouse-only affordance. */
    onFocus: showZoom,
    onBlur: hideZoom,
  };

  return (
    <div className="flex-none" style={{ width }}>
      {onOpen ? (
        <button
          ref={cardRef as React.Ref<HTMLButtonElement>}
          type="button"
          onClick={() => onOpen(alert)}
          title={`${name}${at ? ` at ${at}` : ''} — open alert`}
          className={shell}
          {...hover}
        >
          {body(frameRef)}
        </button>
      ) : (
        <div ref={cardRef as React.Ref<HTMLDivElement>} className={shell} {...hover}>
          {body(frameRef)}
        </div>
      )}
      {/* The enlargement gets its own copy WITHOUT the observer ref — see `body`. It needs no
          observer of its own: it only ever exists for a card already on screen, and it draws the
          preview that card already resolved. */}
      {zoom && (
        <CardZoom anchor={zoom} edge={edge} sev={sev}>
          {body()}
        </CardZoom>
      )}
    </div>
  );
}

/**
 * THE ENLARGEMENT — the same card, redrawn at `ZOOM_W` over the one the pointer is on.
 *
 * IN A PORTAL, and `fixed` rather than absolute. The cards live in a horizontal scroller, and a
 * scroller clips BOTH axes whatever `overflow-y` says — so anything that grew inside the row was
 * cut off by the strip's own height, which is what hid the enlargement behind the panel's upper
 * border. A portal off the anchor's measured rectangle is outside every clipping context in the
 * panel, so it can be as large as the viewport allows.
 *
 * IT REUSES THE CARD'S OWN CHILDREN, so there is nothing to keep in sync: the picture is the one
 * the thumbnail already resolved (no second fetch, which on the cloud is a second blob through
 * the tunnel), and every line of type is set at its own size rather than being a scaled-up 10px
 * label. The frame simply gets more pixels.
 *
 * ANCHORED TO THE EDGE THE CARD IS ALREADY AGAINST — a card at the left of the row grows right,
 * one at the right grows left, and everything between grows from its left edge — then clamped so
 * it can never leave the viewport. Growing from the centre would push the row's outermost cards
 * off screen, which is the one place the enlargement is least recoverable.
 *
 * IT GROWS DOWNWARD from the card's own top, so the rail marker stays directly above the card it
 * names and the panel's head is never covered. If there isn't room below, it is pulled up — a
 * half-visible enlargement is worse than one on the "wrong" side.
 *
 * `pointer-events-none` throughout: the enlargement must never take the hover it was summoned by,
 * or swallow a click aimed at the card underneath it — that card is the button that opens the
 * alert.
 */
function CardZoom({
  anchor,
  edge,
  sev,
  children,
}: {
  anchor: { left: number; top: number; width: number };
  edge: 'start' | 'end' | 'middle';
  sev: (typeof SEV)[string];
  children: React.ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Measured, then placed — the card's height isn't known until its text has wrapped or not.
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const b = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Right-anchored at the row's right-hand end, left-anchored everywhere else.
    let left = edge === 'end' ? anchor.left + anchor.width - b.width : anchor.left;
    left = Math.max(ZOOM_GAP, Math.min(left, vw - b.width - ZOOM_GAP));

    // Down from the card's own top, pulled back up only if it would run off the bottom.
    let top = anchor.top;
    if (top + b.height > vh - ZOOM_GAP) top = vh - b.height - ZOOM_GAP;
    top = Math.max(ZOOM_GAP, top);

    setPos({ left, top });
  }, [anchor, edge]);

  const width = Math.min(ZOOM_W, typeof window === 'undefined' ? ZOOM_W : window.innerWidth - ZOOM_GAP * 2);

  return createPortal(
    <div
      ref={boxRef}
      aria-hidden
      style={{
        width,
        left: pos?.left ?? anchor.left,
        top: pos?.top ?? anchor.top,
        // Invisible for the one frame it is being measured in, so it is never seen at the wrong
        // place before it is placed at the right one.
        opacity: pos ? 1 : 0,
      }}
      className={`pointer-events-none fixed z-[9998] overflow-hidden rounded-xl border border-border border-t-[3px] bg-card p-2 shadow-modal transition-opacity duration-100 ${sev.border}`}
    >
      {children}
    </div>,
    document.body,
  );
}

/** A card whose alert hasn't arrived yet — identical geometry, so nothing shifts when the page
 *  lands. Its top arc takes the rail's own grey: a placeholder has no severity to report, and a
 *  coloured one would be a claim about an alert nobody has seen. */
function CardSkeleton({ width }: { width: number }) {
  return (
    <div className="flex-none" style={{ width }}>
      <div className={`overflow-hidden rounded-xl border border-border border-t-[3px] border-t-slate-200 bg-card p-2 dark:border-t-slate-700`}>
        <div className="w-full animate-pulse rounded-lg bg-muted" style={{ aspectRatio: '16 / 9' }} />
        <div className="mt-2.5 h-4 w-20 animate-pulse rounded bg-muted" />
        <div className="mt-2 h-3 w-3/4 animate-pulse rounded bg-muted" />
        <div className="mt-3 h-3 w-1/2 animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}

/* ─── The panel ──────────────────────────────────────────────────────────── */

export default function LatestAlertsStrip({
  alerts,
  open,
  loading = false,
  stats,
  live,
  minCardWidth = MIN_CARD_W,
  maxCards = 8,
  places,
  loadPreview,
  onOpenAlert,
  onOpenBoard,
  onViewAll,
  onLoadMore,
  loadingMore = false,
  className = '',
}: LatestAlertsStripProps) {
  // "2m ago" is a lie the moment it's painted. One re-render a minute keeps the whole panel
  // honest for the cost of a timer, and there is no cheaper place to put it than here.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  /**
   * WHICH SEVERITIES THE PANEL IS SHOWING. Local state, not a prop: it selects among alerts this
   * panel already holds, so lifting it to the page would make every dashboard wire up a control
   * that changes nothing outside here. Filtering narrows what is DRAWN, never what is LOADED.
   */
  const [sev, setSev] = useState<SevFilter>('all');

  // How much room the row has. Measured on the panel's BODY, which is present in every state:
  // measuring the row itself would lose the observer each time the panel passed through its
  // empty state and come back at zero.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /**
   * How many cards fit, and how wide each one is.
   *
   * The row divides its width evenly between as many cards as will fit at `minCardWidth`, then
   * lets them share out the remainder — so the row is flush at any window width instead of
   * leaving a ragged margin. `MAX_CARD_W` stops a wide display from stretching five cards across
   * a wall: past that the answer is MORE alerts, not bigger ones. Before the first measurement a
   * single card is assumed — one card that widens is a smaller jolt than four that vanish.
   */
  const { capacity, cardW } = useMemo(() => {
    if (!width) return { capacity: 1, cardW: minCardWidth };
    const fit = Math.max(1, Math.floor((width + CARD_GAP) / (minCardWidth + CARD_GAP)));
    const cap = Math.min(fit, maxCards);
    const even = (width - (cap - 1) * CARD_GAP) / cap;
    return { capacity: cap, cardW: Math.min(MAX_CARD_W, Math.max(minCardWidth, even)) };
  }, [width, minCardWidth, maxCards]);

  const filtering = sev !== 'all';
  /** What the panel actually draws. Identical to `alerts` unless a severity is selected. */
  const shown = useMemo(
    () => (sev === 'all' ? alerts : alerts.filter((a) => a.severity === sev)),
    [alerts, sev],
  );
  /** How many of each severity are in the loaded feed — the counts on the chips. */
  const counts = useMemo(() => {
    const c = { critical: 0, warning: 0, normal: 0 } as Record<HexAlert['severity'], number>;
    for (const a of alerts) if (a.severity in c) c[a.severity] += 1;
    return c;
  }, [alerts]);

  /**
   * The size of the queue being paged through — and it is NOT `open` once a severity is chosen.
   * `open` counts every open alert on the node, most of them a severity the row is no longer
   * drawing, so quoting it over three criticals would read as forty-four more waiting behind.
   */
  const total = filtering ? shown.length : open;

  /** Nothing has arrived yet — placeholders, never the empty state: the two must not look the
   *  same, and `open` can land before the feed does. */
  const skeleton = loading && alerts.length === 0;
  const empty = !loading && shown.length === 0;
  /** Empty because of the CHIP, not because the node is quiet — a different sentence, and the
   *  only one of the two with a way out of it. */
  const filteredOut = empty && alerts.length > 0;

  /**
   * Cards standing in for alerts on their way, at the TAIL of the track. Only while a fetch is
   * actually in flight: a placeholder that persists because a request failed reads as a broken
   * card, and the readout already says how many alerts are unaccounted for.
   *
   * ONE under a severity filter, however many are unaccounted for — `open - alerts.length` counts
   * unloaded alerts of every severity, and almost none of the page on its way will be the one the
   * chip selected. A screenful of placeholders behind three criticals promises a screenful of
   * criticals, beside a readout that says "1–3 of 3".
   */
  const pending = loadingMore
    ? filtering
      ? 1
      : Math.max(0, Math.min(capacity, open - alerts.length))
    : 0;

  /**
   * ── THE SLIDER ──
   *
   * The whole loaded queue is in the track's DOM and the track SCROLLS, rather than the row being
   * re-sliced per page. That is what makes it a slider: dragged with a finger, thrown with a
   * trackpad, nudged with the arrows, and continuous under all three — a paged row re-mounts its
   * cards on every step, which drops their resolved previews and makes the gesture feel like
   * navigation instead of movement. Previews are still only fetched for cards that come into
   * view, so holding the whole feed in the DOM costs nothing extra (see `AlertCard`).
   *
   * `scroll` is the measurement everything downstream agrees on: the `1–5 of 9,162` readout, the
   * arrows' enabled state, and the end-of-track fetch. Reading it once and sharing it is what
   * stops those three from disagreeing about where the row is.
   */
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [scroll, setScroll] = useState({ left: 0, view: 0, total: 0 });
  const readScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setScroll((prev) =>
      prev.left === el.scrollLeft && prev.view === el.clientWidth && prev.total === el.scrollWidth
        ? prev
        : { left: el.scrollLeft, view: el.clientWidth, total: el.scrollWidth },
    );
  }, []);

  /** Re-measure whenever the track's contents or the available width change. Scrolling itself is
   *  handled by the scroll event; this is for everything that resizes the track under it. */
  useEffect(() => {
    readScroll();
  }, [readScroll, width, capacity, shown.length, pending]);

  /** A new severity is a new queue: it starts at its newest alert, not at whatever pixel the
   *  previous one happened to be scrolled to. */
  useEffect(() => {
    const el = trackRef.current;
    if (el) el.scrollLeft = 0;
    readScroll();
  }, [sev, readScroll]);

  /** One card plus its gap — the unit every position is quoted in, and the distance a card must
   *  move for the readout to tick over by one. */
  const pitch = cardW + CARD_GAP;

  /** More cards than the box holds, or a node with more to hand over: either way the row is a
   *  track rather than a static shelf. */
  const scrollable = !empty && !skeleton && (shown.length + pending > capacity || shown.length < total);

  /** Which alerts are on screen, in the operator's terms — card-pitch arithmetic, so the readout
   *  moves one alert at a time as the track does. */
  const start = pitch ? Math.max(0, Math.round(scroll.left / pitch)) : 0;
  const visible = Math.min(capacity, Math.max(1, shown.length - start));

  /** A one-pixel tolerance, because fractional widths and device pixel ratios mean a fully
   *  scrolled track rarely lands on the exact number. */
  const canPrev = scrollable && scroll.left > 1;
  const canNext = scrollable && (scroll.left + scroll.view < scroll.total - 1 || alerts.length < open);

  /** A screenful at a time for the arrows and the arrow keys — the same distance either way, so
   *  keyboard and pointer move the row in the same units. */
  const nudge = useCallback((delta: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: delta * Math.max(el.clientWidth, 1), behavior: 'smooth' });
  }, []);

  /**
   * FETCH AS THE TRACK RUNS OUT, not before.
   *
   * Two triggers, because there are two ways to reach the end of what is loaded: scrolling to
   * within a card of it, and a feed so short it cannot be scrolled at all (a node whose count says
   * twenty are open while its live feed handed over three). Without the second, that node would
   * sit at three alerts forever with no gesture available to ask for the rest.
   */
  useEffect(() => {
    if (!onLoadMore || loadingMore || alerts.length >= open) return;
    // `empty` counts as unscrollable and is checked FIRST: a filter that matched nothing unmounts
    // the track entirely, leaving `scroll` holding the last measurement of a track that no longer
    // exists — the one state where the row most needs to fetch is the one where the pixels would
    // have said it didn't.
    const unscrollable = empty || scroll.total <= scroll.view + 1;
    const nearEnd = scroll.left + scroll.view >= scroll.total - Math.max(pitch, 1);
    // Scrolling to the end is the operator asking, and it is always answered.
    if (nearEnd) {
      onLoadMore();
      return;
    }
    // A track too short to scroll asks on its own — which under a severity filter is how a row
    // showing no criticals goes and finds some. Bounded, for the reason FILTER_SCAN_MAX gives.
    if (unscrollable && (!filtering || alerts.length < FILTER_SCAN_MAX)) onLoadMore();
  }, [onLoadMore, loadingMore, alerts.length, open, scroll, pitch, filtering, empty]);

  /** Every card in the track — the loaded queue, then whatever is on its way. `null` is a
   *  placeholder, so the rail and the row iterate one list and cannot fall out of step. */
  const track = useMemo<Array<HexAlert | null>>(
    () => [...shown, ...Array.from({ length: pending }, () => null)],
    [shown, pending],
  );

  /** The alert BEFORE each card, so the rail can write the interval between them. */
  const prevOf = useCallback((i: number) => shown[i - 1]?.timestamp, [shown]);

  /* ── The vitals ── */
  const hour = lastHour(stats?.pulse);
  const busiest = busiestPlace(alerts, places);
  const gap = medianGap(alerts);
  const review = stats?.unacked;
  const oldest = ageOf(stats?.oldest);
  const pulse = stats?.pulse;
  /**
   * Is there anything to PLOT — not merely a series to iterate.
   *
   * This was `pulse.series.values.length`, which is the wrong question and always true: the series
   * is dense by construction (see `foldStepped`), so a completely quiet twelve hours still arrives
   * as forty-nine buckets of zero. The chart then drew forty-nine invisible columns under a
   * "Peak 0", which reads exactly like a chart that is broken rather than a site that was calm —
   * and "no data" and "no alerts" are precisely the two things a reader must be able to tell
   * apart here. `peak` is the node's own maximum over the window, so this asks the real question.
   */
  const hasPulse = !!pulse && pulse.peak > 0;

  return (
    <section className={`overflow-hidden rounded-xl border border-border bg-card shadow-card ${className}`}>
      {/* ── HEAD ── */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <h2 className="text-[15px] font-bold text-foreground">Alerts dashboard</h2>
          {open > 0 &&
            (onViewAll ? (
              <button
                type="button"
                onClick={onViewAll}
                title="View all open alerts"
                className={`${PILL} flex items-center gap-1.5 transition-colors hover:bg-destructive hover:text-white`}
              >
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-destructive" />
                {n(open)} open
              </button>
            ) : (
              <span className={`${PILL} flex items-center gap-1.5`}>
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-destructive" />
                {n(open)} open
              </span>
            ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/*
            THE SEVERITY FILTER — a segmented track rather than a select, because the options are
            half the information: "Critical 2" is read before anything is clicked, and behind a
            dropdown that number would only appear after the operator went looking for it.

            Single-choice with an always-lit "All", so the row can never be in a state whose
            meaning has to be worked out from which chips AREN'T on.

            Hidden while the feed is still arriving: chips reading zero beside a row of
            placeholders say "no criticals" about a panel that has been told nothing yet.
          */}
          {!skeleton && alerts.length > 0 && (
            <div
              role="radiogroup"
              aria-label="Filter alerts by severity"
              className="inline-flex flex-none items-center gap-0.5 rounded-lg bg-muted p-0.5"
            >
              {SEV_FILTERS.map((f) => {
                const active = f.key === sev;
                const count = f.key === 'all' ? alerts.length : counts[f.key];
                return (
                  <button
                    key={f.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSev(f.key)}
                    title={
                      f.key === 'all'
                        ? 'Every open alert'
                        : `${f.label} only — ${count} loaded${count === 0 ? ', the panel will look further back' : ''}`
                    }
                    /* Selected is a raised CARD chip, not a severity fill: a chip flooded red for
                       "Critical" would be the loudest red on a panel whose whole job is to make
                       the alerts' own red the thing you see first. */
                    className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors ${
                      active
                        ? 'bg-card text-foreground shadow-sm ring-1 ring-inset ring-border'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {f.dot && (
                      <span
                        aria-hidden
                        /* Dimmed at zero, so "none of these" is legible from the chip alone
                           without it going quiet enough to look disabled. */
                        className={`h-1.5 w-1.5 flex-none rounded-full ${f.dot} ${count === 0 ? 'opacity-40' : ''}`}
                      />
                    )}
                    <span className="whitespace-nowrap">{f.label}</span>
                    <span className={`tabular-nums ${active ? 'text-muted-foreground' : 'opacity-70'}`}>
                      {n(count)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* IS THE FEED LIVE. A statement about the SUBSCRIPTION, not about arrivals: a quiet
              night and a dropped socket look identical on a still panel, and only one of them
              means what is on screen is stale. Amber when it isn't, because a reconnecting
              dashboard is a thing to notice — but never red, which on this panel means an alert. */}
          {live != null && (
            <span
              title={live ? 'Receiving alerts as they happen' : 'Not connected — this panel may be out of date'}
              className={`flex flex-none items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[12px] font-semibold ${
                live
                  ? 'border-amber-400 text-amber-700 dark:border-amber-500/60 dark:text-amber-400'
                  : 'border-border text-muted-foreground'
              }`}
            >
              <span
                aria-hidden
                className={`h-1.5 w-1.5 rounded-full ${live ? 'animate-pulse bg-amber-500' : 'bg-muted-foreground'}`}
              />
              {live ? 'Live' : 'Offline'}
            </span>
          )}

          {onOpenBoard && (
            /* The one filled button on the panel, because it is the one action: everything else
               here reads or filters. Slate rather than brand — brand on this theme is a vermilion
               that reads as red, and a red button on an alert panel looks like an alarm. */
            <button
              type="button"
              onClick={onOpenBoard}
              className="flex flex-none items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-[12px] font-bold text-white transition-colors hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600"
            >
              Alert board
              <ArrowRightIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* ── VITALS ──
          `divide-x` rather than a border per cell, so the rules between them are exactly one
          hairline wide however the row wraps. The pulse takes two columns on a wide screen and
          drops under the readings on a narrow one, where a 48-bar chart in a quarter of the row
          would be unreadable anyway. */}
      <div className="grid grid-cols-2 divide-x divide-border border-b border-border sm:grid-cols-4 xl:grid-cols-7">
        <Vital
          label="Last hour"
          value={hour ? n(hour.now) : '—'}
          note={hour ? trendNote(hour.now, hour.prev) : 'not reported'}
          hint={
            hour
              ? `${plural(hour.now, 'alert')} raised in the last 60 minutes, across every camera on this node` +
                (hour.prev == null
                  ? '. There is no whole earlier hour in the window to compare it with.'
                  : `, against ${n(hour.prev)} in the hour before. The newest quarter-hour is still filling, so a small fall late in one is normal.`)
              : 'Alerts raised in the last 60 minutes. Reported by the node with the activity chart; not available yet.'
          }
          quiet={!hour}
        />
        <Vital
          label="Busiest zone"
          value={busiest?.name || '—'}
          /* SCOPE, not decoration: this is a reading off the loaded feed, not off the queue. */
          note={busiest ? `${n(busiest.count)} of ${n(alerts.length)} shown` : 'no named zones'}
          hint={
            busiest
              ? `${busiest.name} accounts for ${n(busiest.count)} of the ${plural(alerts.length, 'alert')} loaded here — the newest few dozen, not the whole queue. Cameras with no name or site are not counted.`
              : 'The place producing most of the loaded alerts. None of them have a named camera or site.'
          }
          quiet={!busiest}
        />
        <Vital
          label="Median gap"
          value={gap?.label || '—'}
          /* SCOPE, like the cell beside it: this is measured over the alerts THIS PANEL HOLDS —
             the newest few dozen — not over the whole queue, and a reader who takes it for the
             latter would read a busy ten minutes as the rate of the whole day. */
          note={gap ? `between the ${n(gap.count)} shown` : 'between events'}
          hint={
            gap
              ? `Half the gaps between one alert and the next are shorter than ${gap.label}, half longer. ` +
                `Measured across the ${plural(gap.count, 'alert')} loaded here, spanning ${spanLabel(gap.from, gap.to)}.`
              : 'The typical spacing between one alert and the next. Needs at least two alerts.'
          }
          quiet={!gap}
        />
        <Vital
          label="Needs review"
          value={review == null ? '—' : n(review)}
          note={oldest ? `oldest ${oldest}` : review == null ? 'not reported' : 'nothing waiting'}
          hint={
            review == null
              ? 'Open alerts nobody has acknowledged yet. Counted by the node across the whole queue; not available yet.'
              : `${plural(review, 'open alert')} still in "new" — nobody has picked ${review === 1 ? 'it' : 'them'} up. ` +
                `Counted by the node across the WHOLE queue, not just what is on this panel` +
                (oldest ? `. The oldest has been waiting ${oldest}.` : '.')
          }
          quiet={review == null}
        />
        <div className="col-span-2 min-w-0 px-4 py-3 sm:col-span-4 xl:col-span-3">
          <div className="flex items-baseline justify-between gap-2">
            <p className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {pulse ? `Alerts per ${Math.round((pulse.series.step || 900) / 60)} min · last ${pulse.hours} h` : 'Alert activity'}
            </p>
            {/* The scale, stated. Without it the bars are a shape with no magnitude: a reader can
                see that eight o'clock was busier than nine and cannot tell whether the tallest bar
                is four alerts or forty. Taken from the node so it and the chart agree.

                Only where there IS a scale: "Peak 0" over an empty plot is a label describing
                nothing, and it was the only thing on screen during a quiet twelve hours. */}
            {hasPulse && (
              <p className="flex-none text-[10px] font-semibold uppercase tracking-[0.08em] tabular-nums text-muted-foreground">
                Peak {n(pulse!.peak)}
              </p>
            )}
          </div>
          {hasPulse ? (
            <>
              <div className="mt-2">
                <MiniBars series={pulse!.series} color="var(--brand)" emphasis="last" height={44} unit="alerts" />
              </div>
              <div className="mt-1 flex items-baseline justify-between text-[10px] tabular-nums text-muted-foreground">
                <span>{fmtClock(pulse!.series.start, false)}</span>
                <span>now</span>
              </div>
            </>
          ) : (
            <p className="mt-2 text-[11px] leading-[15px] text-muted-foreground">
              {!pulse
                ? 'Not reported by this node.'
                : `No alerts at all in the last ${pulse.hours} hours — nothing to plot.`}
            </p>
          )}
        </div>
      </div>

      {/* ── TIMELINE HEAD ── */}
      {/* The band that separates the readings above from the queue below. `slate-50` — a ~2%
          tint, which is what the reference shows and roughly all a divider strip needs when it
          already has a rule under it. It was `bg-muted` (`#E2E8EF`, a 12% grey): heavy enough to
          read as a second toolbar competing with the panel's actual head. Dark mode takes the
          same relationship rather than the same colour — one step recessed from the card, which
          on `#1a1e2a` means darker. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b border-border bg-slate-50 px-4 py-2 dark:bg-slate-900">
        <p className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-muted-foreground">
          <span className="font-semibold uppercase tracking-[0.08em] text-foreground">Timeline · newest first</span>
          {!empty && !skeleton && (
            <span className="tabular-nums">
              {n(start + 1)}–{n(start + visible)} of {n(total)} · numbers on the rail are gaps between events
            </span>
          )}
        </p>
        {!empty && !skeleton && scrollable && (
          /* The arrows NUDGE the slider — they are not a pager. The row is one continuous track
             an operator drags or throws; these are the keyboard-and-mouse way to move it by the
             same screenful, for the people not using a trackpad. Disabled rather than hidden at
             the ends, so the pair keeps its position and says there is nothing that way instead
             of vanishing from under the pointer. */
          <div className="flex flex-none items-center gap-1.5">
            <PagerButton label="Newer alerts" disabled={!canPrev} onClick={() => nudge(-1)}>
              <ChevronLeftIcon className="h-4 w-4" />
            </PagerButton>
            <PagerButton label="Older alerts" disabled={!canNext} onClick={() => nudge(1)}>
              <ChevronRightIcon className="h-4 w-4" />
            </PagerButton>
          </div>
        )}
      </div>

      {/* ── THE TIMELINE ── */}
      <div ref={bodyRef} className="px-4 py-3">
        {empty ? (
          /* Deliberately one line, not a row of empty frames: an all-clear machine should read
             as calm. `bg-muted` and not `bg-muted/40`, which compiled to no rule at all and left
             exactly the card-on-card panel the fill is there to avoid. */
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border bg-muted px-4 py-3 text-[13px] text-muted-foreground">
            {filteredOut ? (
              <>
                {/* NOT the all-clear tick: the node is not clear, the operator is looking at a
                    slice of it, and a green check over a queue holding warnings is a lie. */}
                <FunnelIcon className="h-4 w-4 flex-none" aria-hidden />
                <span>
                  No {SEV_FILTERS.find((f) => f.key === sev)?.label.toLowerCase()} alerts among the{' '}
                  {n(alerts.length)} loaded{loadingMore ? ' — looking further back…' : ''}.
                </span>
                <button
                  type="button"
                  onClick={() => setSev('all')}
                  className="rounded-md border border-border bg-card px-2 py-0.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
                >
                  Show all
                </button>
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4 flex-none text-emerald-500" aria-hidden />
                <span>No open alerts.</span>
              </>
            )}
          </div>
        ) : (
          /*
            THE SCROLL BOX. The rail and the cards are BOTH inside it, on one column grid, so a
            marker stays over its card at every scroll offset — a rail outside the box would sit
            still while the queue slid under it.

            NOTHING CAN ESCAPE THIS BOX, and an earlier pass claimed otherwise. `overflow-x-auto`
            with `overflow-y-visible` does NOT scroll sideways while letting content overhang
            vertically: CSS computes `visible` to `auto` on the other axis the moment one axis is
            not `visible`, so the pair is really `auto`/`auto` and a hovered card that grew was
            simply clipped — which is what put the enlargement "under the upper border". A card's
            enlargement therefore goes in a PORTAL (`CardZoom`), outside every clipping context in
            the panel, and this box stays a plain horizontal scroller.

            `scroll-smooth` so the arrows glide rather than jump; `snap-x` so a thrown track comes
            to rest with a card at the left edge instead of mid-picture. `overscroll-x-contain`
            keeps a horizontal fling from turning into a browser back-navigation.
          */
          <div
            ref={trackRef}
            onScroll={readScroll}
            role="group"
            aria-label="Alert timeline"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') { e.preventDefault(); nudge(1); }
              if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(-1); }
            }}
            className="snap-x snap-mandatory overflow-x-auto overscroll-x-contain scroll-smooth pb-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <div className="w-max min-w-full">
              <Rail
                items={skeleton ? Array.from({ length: capacity }, () => null) : track}
                cardW={cardW}
                prevOf={skeleton ? () => undefined : prevOf}
              />
              <div className="flex items-start" style={{ gap: CARD_GAP }}>
                {skeleton
                  ? Array.from({ length: capacity }, (_, i) => <CardSkeleton key={`s${i}`} width={cardW} />)
                  : track.map((a, i) =>
                      a ? (
                        <div key={a.id || `${a.cameraId}-${a.timestamp}`} className="snap-start">
                          <AlertCard
                            alert={a}
                            place={places?.[a.cameraId]}
                            width={cardW}
                            /* Where this card sits in the VISIBLE window, so the hover grows
                               inwards at the edges rather than off them. */
                            edge={i === start ? 'start' : i === start + visible - 1 ? 'end' : 'middle'}
                            loadPreview={loadPreview}
                            onOpen={onOpenAlert}
                          />
                        </div>
                      ) : (
                        <CardSkeleton key={`p${i}`} width={cardW} />
                      ),
                    )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * A pager arrow. Round, bordered and on the card surface, which is what makes it read as a
 * control on a muted strip — and disabled rather than hidden at the ends, so the pair keeps its
 * position and the operator can see there is nothing that way rather than watching a button
 * disappear from under the pointer.
 */
function PagerButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-card text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-card"
    >
      {children}
    </button>
  );
}
